import { Layout } from '@/components/layouts/layout';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { useAuctions, useDeleteAuction } from '@/hooks/use-auctions';
import { getPlatformColor } from '@/types/auctions';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { ChevronRight, Clock, Package, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

export const Route = createFileRoute('/auctions/')({
    component: AuctionsPage,
});

type TabKey = 'Active' | 'Upcoming' | 'Past';

function AuctionsPage() {
    const [tab, setTab] = useState<TabKey>('Active');
    const [deleteTarget, setDeleteTarget] = useState<{ id: string; title: string } | null>(null);
    const { data: auctions = [], isLoading } = useAuctions();
    const navigate = useNavigate();
    const deleteMutation = useDeleteAuction();

    function confirmDelete() {
        if (!deleteTarget) return;
        deleteMutation.mutate(deleteTarget.id, {
            onSuccess: () => { toast.success('Auction deleted'); setDeleteTarget(null); },
            onError: () => toast.error('Failed to delete auction'),
        });
    }

    const active = auctions.filter((a) => a.status === 'active');
    const upcoming = auctions.filter((a) => a.status === 'draft');
    const past = auctions.filter((a) => a.status === 'completed');

    const displayed =
        tab === 'Active' ? active : tab === 'Upcoming' ? upcoming : past;

    const tabs: { label: string; key: TabKey; count: number }[] = [
        { label: 'Active', key: 'Active', count: active.length },
        { label: 'Upcoming', key: 'Upcoming', count: upcoming.length },
        { label: 'Past', key: 'Past', count: past.length },
    ];

    return (
        <Layout>
            <div className="max-w-6xl mx-auto px-4 py-8 pb-24">
                {/* Header */}
                <div className="flex items-end justify-between mb-7">
                    <div>
                        <h1 className="text-[32px] font-bold tracking-tight mb-1.5 [font-family:var(--font-heading)] text-[#f5f7fb]">
                            Auctions
                        </h1>
                        <p className="text-[#9aa2c0] text-[14.5px]">
                            Run live auction events and sync sold items back to
                            inventory.
                        </p>
                    </div>
                    <Link to="/auctions/new">
                        <button className="flex items-center gap-2 font-semibold text-sm text-white rounded-[10px] px-4.25 py-2.75 cursor-pointer transition-colors bg-primary hover:bg-primary/60">
                            <Plus size={17} strokeWidth={2.5} />
                            New Auction
                        </button>
                    </Link>
                </div>

                {/* Tabs */}
                <div className="flex gap-1 p-1 w-fit mb-6 rounded-[11px] bg-[#0c111d] border border-[#1b2235]">
                    {tabs.map((t) => (
                        <button
                            key={t.key}
                            onClick={() => setTab(t.key)}
                            className={`px-4.5 py-2 rounded-xl text-[13.5px] font-semibold cursor-pointer transition-colors ${tab === t.key ? 'text-white bg-[#1a2235]' : 'text-[#9aa2c0] bg-transparent'}`}
                        >
                            {t.label}
                            {t.count > 0 && (
                                <span
                                    className={`ml-1.5 text-[11px] font-bold px-1.5 py-0.5 rounded-md ${tab === t.key ? 'text-[#9d86ff] bg-[rgba(108,74,240,.18)]' : 'text-[#6b7390] bg-[rgba(255,255,255,.06)]'}`}
                                >
                                    {t.count}
                                </span>
                            )}
                        </button>
                    ))}
                </div>

                {/* List */}
                {isLoading ? (
                    <div className="flex flex-col gap-3.5">
                        {[1, 2].map((i) => (
                            <div
                                key={i}
                                className="h-22 rounded-2xl animate-pulse bg-[#111726]"
                            />
                        ))}
                    </div>
                ) : displayed.length === 0 ? (
                    <div className="rounded-2xl p-12 text-center bg-[#111726] border border-[#232b3f]">
                        <Package
                            size={36}
                            className="mx-auto mb-3 text-[#3a4258]"
                        />
                        <p className="text-[#6b7390] text-sm">
                            {tab === 'Active'
                                ? 'No active auctions. Start one from an upcoming event.'
                                : tab === 'Upcoming'
                                  ? 'No upcoming auctions. Create one to get started.'
                                  : 'No past auctions yet.'}
                        </p>
                    </div>
                ) : (
                    <div className="flex flex-col gap-3.5">
                        {displayed.map((a) => {
                            const soldItems =
                                a.status === 'completed' ? a.itemCount : 0;
                            const platformColor = getPlatformColor(a.platform);
                            const statusChip =
                                a.status === 'active'
                                    ? {
                                          bg: 'rgba(108,74,240,.16)',
                                          fg: '#9d86ff',
                                          label: 'Active',
                                      }
                                    : a.status === 'completed'
                                      ? {
                                            bg: 'rgba(52,210,127,.14)',
                                            fg: '#34d27f',
                                            label: 'Completed',
                                        }
                                      : {
                                            bg: 'rgba(148,163,184,.12)',
                                            fg: '#9aa2c0',
                                            label: 'Draft',
                                        };

                            return (
                                <div
                                    key={a.id}
                                    className={`flex items-center gap-5 rounded-2xl px-5.5 py-5 bg-[#111726] border ${a.status === 'active' ? 'border-[#2c2350]' : 'border-[#1f2638]'}`}
                                >
                                    {/* Platform icon */}
                                    <div
                                        className="w-13 h-13 rounded-[13px] flex items-center justify-center shrink-0"
                                        style={{
                                            background: `linear-gradient(140deg, ${platformColor}, ${platformColor}bb)`,
                                        }}
                                    >
                                        <Package size={22} color="#fff" />
                                    </div>

                                    {/* Info */}
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2.5 mb-1.5">
                                            <span className="font-semibold text-[19px] [font-family:var(--font-heading)] text-[#f5f7fb]">
                                                {a.title}
                                            </span>
                                            <span className="flex items-center gap-1.5 text-[11.5px] font-semibold px-2.25 py-0.75 rounded-[7px] text-[#c3c9de] bg-[#161d2e] border border-[#242c40]">
                                                <span
                                                    className="w-1.75 h-1.75 rounded-full"
                                                    style={{
                                                        background:
                                                            platformColor,
                                                    }}
                                                />
                                                {a.platform}
                                            </span>
                                            <span
                                                className="text-[11px] font-bold tracking-[.4px] uppercase px-2.25 py-0.75 rounded-[7px]"
                                                style={{
                                                    color: statusChip.fg,
                                                    background: statusChip.bg,
                                                }}
                                            >
                                                {statusChip.label}
                                            </span>
                                        </div>
                                        <div className="flex items-center gap-3.5 text-[13.5px] text-[#9aa2c0]">
                                            <span className="flex items-center gap-1.5">
                                                <Clock size={14} />
                                                {a.scheduled_at
                                                    ? new Date(
                                                          a.scheduled_at,
                                                      ).toLocaleDateString(
                                                          'en-US',
                                                          {
                                                              month: 'short',
                                                              day: 'numeric',
                                                              hour: 'numeric',
                                                              minute: '2-digit',
                                                          },
                                                      )
                                                    : a.status ===
                                                            'completed' &&
                                                        a.completed_at
                                                      ? new Date(
                                                            a.completed_at,
                                                        ).toLocaleDateString()
                                                      : 'No date set'}
                                            </span>
                                            <span className="text-[#3a4258]">
                                                •
                                            </span>
                                            <span>{a.itemCount} items</span>
                                            {a.status === 'completed' &&
                                                soldItems > 0 && (
                                                    <>
                                                        <span className="text-[#3a4258]">
                                                            •
                                                        </span>
                                                        <span className="text-[#34d27f] font-semibold">
                                                            Completed
                                                        </span>
                                                    </>
                                                )}
                                        </div>
                                    </div>

                                    {/* Actions */}
                                    <div className="flex items-center gap-2.5 shrink-0">
                                        <button
                                            onClick={() =>
                                                setDeleteTarget({ id: a.id, title: a.title })
                                            }
                                            disabled={deleteMutation.isPending}
                                            className="p-2.25 rounded-[9px] cursor-pointer transition-colors text-[#6b7390] bg-[#161d2e] border border-[#2a3450] hover:text-[#f0556b]"
                                        >
                                            <Trash2 size={15} />
                                        </button>
                                        {a.status === 'active' && (
                                            <>
                                                <button
                                                    onClick={() =>
                                                        navigate({
                                                            to: '/auctions/$auctionId',
                                                            params: {
                                                                auctionId: a.id,
                                                            },
                                                        })
                                                    }
                                                    className="text-[13.5px] font-semibold px-3.75 py-2.5 rounded-[9px] cursor-pointer text-[#cdd3e6] bg-[#161d2e] border border-[#2a3450] hover:bg-[#1e2840]"
                                                >
                                                    Manage
                                                </button>
                                                <button
                                                    onClick={() =>
                                                        navigate({
                                                            to: '/auctions/$auctionId/live',
                                                            params: {
                                                                auctionId: a.id,
                                                            },
                                                        })
                                                    }
                                                    className="flex items-center gap-2 text-[13.5px] font-bold text-white px-4.25 py-2.5 rounded-[9px] cursor-pointer bg-[#f0556b] hover:bg-[#e04060]"
                                                >
                                                    <span className="w-2 h-2 rounded-full bg-white animate-pulse" />
                                                    Go Live
                                                </button>
                                            </>
                                        )}
                                        {a.status === 'draft' && (
                                            <button
                                                onClick={() =>
                                                    navigate({
                                                        to: '/auctions/$auctionId',
                                                        params: {
                                                            auctionId: a.id,
                                                        },
                                                    })
                                                }
                                                className="flex items-center gap-1.5 text-[13.5px] font-semibold px-4 py-2.5 rounded-[9px] cursor-pointer text-[#cdd3e6] bg-[#161d2e] border border-[#2a3450] hover:bg-[#1e2840]"
                                            >
                                                Manage{' '}
                                                <ChevronRight size={15} />
                                            </button>
                                        )}
                                        {a.status === 'completed' && (
                                            <button
                                                onClick={() =>
                                                    navigate({
                                                        to: '/auctions/$auctionId/review',
                                                        params: {
                                                            auctionId: a.id,
                                                        },
                                                    })
                                                }
                                                className="flex items-center gap-1.5 text-[13.5px] font-semibold px-4 py-2.5 rounded-[9px] cursor-pointer text-[#cdd3e6] bg-[#161d2e] border border-[#2a3450] hover:bg-[#1e2840]"
                                            >
                                                View Results{' '}
                                                <ChevronRight size={15} />
                                            </button>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
            <Dialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Delete auction?</DialogTitle>
                        <DialogDescription>
                            "{deleteTarget?.title}" will be permanently deleted. This cannot be undone.
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <button
                            onClick={() => setDeleteTarget(null)}
                            className="px-4 py-2 text-sm font-semibold rounded-[9px] text-[#9aa2c0] bg-[#161d2e] border border-[#2a3450] hover:bg-[#1e2840]"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={confirmDelete}
                            disabled={deleteMutation.isPending}
                            className="px-4 py-2 text-sm font-semibold rounded-[9px] text-white bg-[#f0556b] hover:bg-[#e04060] disabled:opacity-50"
                        >
                            Delete
                        </button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </Layout>
    );
}
